/**
 * Normaliserer et telefonnummer til E.164 (`+4791234567`). Brukes for
 * kontaktpersoner (CRM-1.1); SMS-leddet i fase 4 krever E.164.
 *
 * Støtter norske nummer uten landkode (8 sifre), `+47`/`0047`-prefiks og
 * utenlandske nummer som starter med `+` eller `00`. Returnerer `null` når
 * nummeret ikke kan tolkes sikkert.
 */
export function normalizePhoneE164(raw: string | null | undefined, defaultCountry: "NO" = "NO"): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/[^\d\s()+\-.]/.test(trimmed)) return null;

  const hasPlus = trimmed.startsWith("+");
  let digits = trimmed.replace(/\D/g, "");

  if (hasPlus) {
    // E.164 tillater 8 til 15 sifre inkl. landkode.
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (defaultCountry === "NO" && /^[2-9]\d{7}$/.test(digits)) {
    return `+47${digits}`;
  }
  return null;
}

/**
 * Kan vi sende SMS til dette nummeret? Samme regel som edge-versjonen
 * (_shared/phone.ts smsRecipient): gyldig E.164, og norske numre må være mobil
 * (begynner på 4 eller 9). Brukes til advarselen «SMS-påminnelser hoppes over».
 */
export function isSmsCapable(raw: string | null | undefined): boolean {
  const e164 = normalizePhoneE164(raw);
  if (!e164) return false;
  return !e164.startsWith("+47") || /^\+47[49]\d{7}$/.test(e164);
}

/**
 * Telefonnummer til visning: norske som «481 20 757», alt annet som det er
 * lagret. `tel:` bør bruke `normalizePhoneE164` når den gir svar.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return "";
  const e164 = normalizePhoneE164(raw);
  if (e164 && /^\+47\d{8}$/.test(e164)) {
    const d = e164.slice(3);
    return `${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5)}`;
  }
  return raw.trim();
}
